import { useState, useEffect, useCallback } from "react";
import { CheckResult } from "@/components/UrlChecker/UrlCheckerResult";

const CACHE_KEY = "url_checker_cache";
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 1 day in milliseconds

interface CacheEntry {
  result: CheckResult;
  timestamp: number;
  domain: string;
}

interface CacheData {
  [url: string]: CacheEntry;
}

export function useUrlCache() {
  const [cachedUrls, setCachedUrls] = useState<CacheEntry[]>([]);

  // Load and clean cache on mount
  const loadCache = useCallback((): CacheData => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return {};
      
      const data: CacheData = JSON.parse(cached);
      const now = Date.now();
      
      // Filter out expired entries
      const validData: CacheData = {};
      Object.entries(data).forEach(([url, entry]) => {
        if (now - entry.timestamp < CACHE_DURATION_MS) {
          validData[url] = entry;
        }
      });
      
      // Save cleaned cache
      if (Object.keys(validData).length !== Object.keys(data).length) {
        localStorage.setItem(CACHE_KEY, JSON.stringify(validData));
      }
      
      return validData;
    } catch {
      return {};
    }
  }, []);

  // Update cached URLs list
  const refreshCachedUrls = useCallback(() => {
    const cache = loadCache();
    const entries = Object.values(cache).sort((a, b) => b.timestamp - a.timestamp);
    setCachedUrls(entries);
  }, [loadCache]);

  useEffect(() => {
    refreshCachedUrls();
    
    // Set up interval to check for expired entries every minute
    const interval = setInterval(refreshCachedUrls, 60000);
    return () => clearInterval(interval);
  }, [refreshCachedUrls]);

  // Get cached result for a URL
  const getCachedResult = useCallback((url: string): CheckResult | null => {
    const cache = loadCache();
    const normalizedUrl = url.toLowerCase().trim();
    const entry = cache[normalizedUrl];
    
    if (entry && Date.now() - entry.timestamp < CACHE_DURATION_MS) {
      return entry.result;
    }
    
    return null;
  }, [loadCache]);

  // Save result to cache
  const cacheResult = useCallback((url: string, result: CheckResult) => {
    const cache = loadCache();
    const normalizedUrl = url.toLowerCase().trim();
    
    cache[normalizedUrl] = {
      result,
      timestamp: Date.now(),
      domain: result.domain || new URL(url).hostname,
    };
    
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    refreshCachedUrls();
  }, [loadCache, refreshCachedUrls]);

  // Clear a specific URL from cache
  const clearCacheEntry = useCallback((url: string) => {
    const cache = loadCache();
    const normalizedUrl = url.toLowerCase().trim();
    delete cache[normalizedUrl];
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    refreshCachedUrls();
  }, [loadCache, refreshCachedUrls]);

  // Clear all cache
  const clearAllCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    setCachedUrls([]);
  }, []);

  // Get time remaining until expiry
  const getTimeRemaining = useCallback((timestamp: number): string => {
    const remaining = CACHE_DURATION_MS - (Date.now() - timestamp);
    if (remaining <= 0) return "Expired";
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }, []);

  return {
    cachedUrls,
    getCachedResult,
    cacheResult,
    clearCacheEntry,
    clearAllCache,
    getTimeRemaining,
  };
}
